import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon, MessageSquareIcon } from 'lucide-react';
type Post = {
  id: string;
  username: string;
  avatar: string;
  timestamp: string;
  content: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  similarity: number;
};
type Product = {
  id: number;
  name: string;
  description: string;
  image: string;
  isMatching: boolean;
  intentPhrases: string[];
};
type MatchModalProps = {
  post: Post;
  product: Product;
  onClose: () => void;
  onApprove: () => void;
};
const MatchModal: React.FC<MatchModalProps> = ({
  post,
  product,
  onClose,
  onApprove
}) => {
  // Generate a random AI reply based on the post and product
  const generateAIReply = () => {
    const replies = [`We have opportunities to volunteer with ${product.name}! DM us for details on how to get involved.`, `Thanks for your interest in ${product.name}! We'd love to have you join our volunteer team.`, `We're always looking for volunteers for our ${product.name} program. Let us know if you'd like more information!`, `Our ${product.name} program needs caring volunteers like you! Would you like to learn more?`];
    return replies[Math.floor(Math.random() * replies.length)];
  };
  const aiReply = generateAIReply();
  const matchConfidence = Math.round(70 + Math.random() * 25);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black bg-opacity-70 flex items-end sm:items-center justify-center z-50 p-4" data-id="element-17">
      <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="bg-gray-800 rounded-lg w-full max-w-lg overflow-hidden" data-id="element-18">
        <div className="p-4 bg-gray-700 flex justify-between items-center" data-id="element-19">
          <h3 className="text-lg font-medium" data-id="element-20">Volunteer Match Detected</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-21">
            <XIcon className="w-5 h-5" data-id="element-22" />
          </button>
        </div>
        <div className="p-4 space-y-4" data-id="element-23">
          <div className="space-y-2" data-id="element-24">
            <div className="text-sm text-gray-400" data-id="element-25">Social Post</div>
            <div className="p-3 bg-gray-700 rounded-lg" data-id="element-26">
              <div className="flex items-center gap-2 mb-1" data-id="element-27">
                <div className="w-6 h-6 rounded-full overflow-hidden" data-id="element-28">
                  <img src={post.avatar} alt={post.username} className="w-full h-full object-cover" data-id="element-29" />
                </div>
                <div className="font-medium text-sm" data-id="element-30">@{post.username}</div>
              </div>
              <div className="text-sm" data-id="element-31">{post.content}</div>
            </div>
          </div>
          <div className="space-y-2" data-id="element-32">
            <div className="text-sm text-gray-400" data-id="element-33">Detected Intent</div>
            <div className="p-2 bg-gray-700 rounded-lg text-sm" data-id="element-34">
              "{product.intentPhrases[0]}"
            </div>
          </div>
          <div className="space-y-2" data-id="element-35">
            <div className="text-sm text-gray-400" data-id="element-36">
              Matched Volunteer Activity
            </div>
            <div className="p-3 bg-gray-700 rounded-lg flex items-center gap-3" data-id="element-37">
              <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0" data-id="element-38">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-39" />
              </div>
              <div data-id="element-40">
                <div className="font-medium" data-id="element-41">{product.name}</div>
                <div className="text-xs text-gray-400" data-id="element-42">
                  {product.description}
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2" data-id="element-43">
            <div className="text-sm text-gray-400" data-id="element-44">AI Reply Suggestion</div>
            <div className="p-3 bg-blue-900 bg-opacity-30 rounded-lg border border-blue-800 text-sm relative" data-id="element-45">
              <MessageSquareIcon className="w-4 h-4 absolute top-2 left-2 text-blue-400" data-id="element-46" />
              <div className="pl-6" data-id="element-47">{aiReply}</div>
            </div>
          </div>
          <div className="flex justify-between items-center" data-id="element-48">
            <div className="text-sm" data-id="element-49">
              <span className="text-gray-400" data-id="element-50">Match confidence: </span>
              <span className={`font-medium ${matchConfidence > 85 ? 'text-green-400' : matchConfidence > 75 ? 'text-yellow-400' : 'text-gray-300'}`} data-id="element-51">
                {matchConfidence}%
              </span>
            </div>
            <div className="flex gap-2" data-id="element-52">
              <button onClick={onClose} className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded" data-id="element-53">
                Ignore
              </button>
              <button onClick={onApprove} className="px-3 py-1 text-sm bg-green-600 hover:bg-green-500 rounded flex items-center gap-1" data-id="element-54">
                <CheckIcon className="w-4 h-4" data-id="element-55" />
                Approve & Post
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
};
export default MatchModal;